import moment from 'moment';
import BaseModel from './BaseModel';
import TagModel from './TagModel';
import { PropertyLocationModel } from './LocationModels';
import ProjectModel from './ProjectModel';
import CompanyEquipmentModel from './CompanyEquipmentModel';
import UserModel from './UserModel';
import VehicleModel from './VehicleModel';
import { ReservationStatus, ReservationStatusUi } from './ReservationModel';
import { DJANGO_DATE_FORMAT } from '../constants/formatStrings.js';

class UiSettingsModel extends BaseModel {
  init(data) {
    this.search = data.search || '';
    this.sort = data.sort || 'name';
    this.tag_filters = (data.tag_filters || []).map(x => new TagModel(x));
    this.ui_selected_fields = data.ui_selected_fields || [];
    this.ui_view_type = data.ui_view_type || 'grid';
  }

  get is_grid_view() {
    if (this.ui_view_type) {
      return this.ui_view_type === 'grid';
    }
    return true;
  }

  set is_grid_view(v) {
    this.ui_view_type = v ? 'grid' : 'table';
  }

  get locationComputed() {
    return this.locations?.length
      ? this.locations[0]
      : 0;
  }

  set locationComputed(val) {
    if (!this.locations) this.locations = [];

    if (typeof this.locationFilterUpdated === 'function') {
      this.locationFilterUpdated(this.locations[0] || 0, val);
    } else {
      this.locations[0] = val;
    }
  }

  reset() {
    this.search = '';

    // only reset if there are selected tags, otherwise watcher will fire needlessly
    if (this.tag_filters.length) {
      this.tag_filters = [];
    }
  }
}

class PMScheduleUiSettingsModel extends UiSettingsModel {
  init(data) {
    super.init(data);

    this.task_categories = data.task_categories || []; // id/name
    this.assignees = (data.assignees || []).map(u => new UserModel(u));
    this.priorities = data.priorities ? data.priorities.map(p => parseInt(p)) : [];
    this.projects = (data.projects || []).map(p => new ProjectModel(p));
    this.locations = (data.locations || []).map(l => new PropertyLocationModel(l));
    this.equipment = (data.equipment || []).map(e => new CompanyEquipmentModel(e));
    this.vehicles = (data.vehicles || []).map(v => new VehicleModel(v));
    this.active = 'active' in data ? (data.active ?? '').toString() : null;
    this.schedule = data.schedule || '';
  }

  reset(includeAssignees = false) {
    super.reset();

    if (includeAssignees) {
      this.assignees = [];
    }

    this.task_categories = [];
    this.priorities = [];
    this.projects = [];
    this.locations = [];
    this.equipment = [];
    this.vehicles = [];
    this.active = null;
    this.schedule = '';
  }

  get assigneeUserIds() {
    return this.assignees ? this.assignees.map(user => (user.id || user)) : [];
  }
}

class EquipmentUiSettingsModel extends UiSettingsModel {
  init(data) {
    super.init(data);

    this.location_filters = (data.location_filters || []).map(x => new PropertyLocationModel(x));
    this.equipment_category_filters = data.equipment_category_filters || [];
    this.status_filters = data.status_filters || [];
    this.qr_code_assigned_filter = data.qr_code_assigned_filter;
  }

  get rfp_filter() {
    const rfp = this.rfp_filters.length ? this.rfp_filters[0] : null;
    return rfp?.id || rfp;
  }

  set rfp_filter(v) {
    this.rfp_filters = v ? [v] : [];
  }

  get status_filter() {
    return this.status_filters.length ? this.status_filters[0] : null;
  }

  set status_filter(v) {
    this.status_filters = v ? [v] : [];
  }

  get location_filter() {
    const location = this.location_filters.length ? this.location_filters[0] : null;
    return location?.id || location || 0;
  }

  set location_filter(v) {
    this.location_filters = v ? [v] : [];
  }

  get equipment_type() {
    const equipmentType = this.equipment_category_filters.length ? this.equipment_category_filters[0] : null;
    return equipmentType?.id || equipmentType || 0;
  }

  set equipment_type(v) {
    this.equipment_category_filters = v ? [v] : [];
  }

  reset() {
    super.reset();

    this.rfp_filter = null;
    this.status_filter = null;
    this.location_filter = null;
    this.equipment_type = '';
    this.qr_code_assigned_filter = null;
  }
}

class LocationUiSettingsModel extends UiSettingsModel {
  init(data) {
    super.init(data);

    this.location_category = data.location_category?.id ?? data.location_category ?? 0;
  }

  reset() {
    super.reset();

    this.location_category = 0;
  }
}

class PartUiSettingsModel extends UiSettingsModel {
  init(data) {
    super.init(data);

    this.equipment = data.equipment?.id ?? data.equipment ?? 0;
    this.location = data.location?.id ?? data.location ?? 0;
    this.vehicle = data.vehicle?.id ?? data.vehicle ?? 0;
  }

  reset() {
    super.reset();

    this.equipment = 0;
    this.location = 0;
    this.vehicle = 0;
  }
}

class PartContainerUiSettingsModel extends UiSettingsModel {
  init(data) {
    super.init(data);

    this.location = data.location?.id ?? data.location ?? 0;
  }

  reset() {
    super.reset();

    this.location = 0;
  }
}

class WorkOrderRequestUiSettingsModel extends UiSettingsModel {
  init(data) {
    super.init(data);

    this.statuses = data.statuses || null;
    this.categories = data.task_categories || []; // id/name
    this.priorities = data.priorities || [];
    this.requester = data.requester ? new UserModel(data.requester) : null;
    this.locations = (data.locations || []).map(l => l.id);
    this.created_after = data.created_date_after || null;
    this.created_before = data.created_date_before || null;
    this.requested_completion_date_after = data.completion_date_after || null;
    this.requested_completion_date_before = data.completion_date_before || null;
    this.reviewers = (data.reviewers ?? []).map(u => new UserModel(u));
    this.no_assigned_reviewer = data.no_assigned_reviewer ?? null;

    this.locationFilterUpdated = data.locationFilterUpdated ?? null;
  }

  reset(resetReviewerFilters = false) {
    super.reset();

    this.statuses = null;
    this.categories = null;
    this.priorities = [];
    this.requester = 0;
    this.locations = [];
    this.created_after = null;
    this.created_before = null;
    this.requested_completion_date_after = null;
    this.requested_completion_date_before = null;

    if (resetReviewerFilters) {
      this.reviewers = [];
      this.no_assigned_reviewer = null;
    }
  }

  getParamsObj(page, limit, uiViewType = 'all', showManagerView = false) {
    const params = {
      search: this.search,
      ordering: this.sort,
      limit,
      offset: (page - 1) * limit,
      categories: (this.categories || []).map(c => c.id).join(',') || null,
      priorities: this.priorities?.join(',') || null,
      location__ancestor_hierarchy__ancestor_id: this.locations?.length ? this.locations[0] : null,
      created_after: this.created_after,
      created_before: this.created_before,
      requested_completion_date_after: this.requested_completion_date_after,
      requested_completion_date_before: this.requested_completion_date_before,
    };

    if (showManagerView) {
      params.requester = this.requester?.id || null;

      const reviewFilter = (this.reviewers || []).map(u => u.id).join(',') || null;

      if (uiViewType === 'all') {
        params.reviewed_by = reviewFilter;
      } else {
        params.no_assigned_reviewer = this.no_assigned_reviewer ?? null;
        params.reviewers = reviewFilter;
      }
    }

    let statuses = (this.statuses || []).filter(s => s.id).map(s => s.id) || null;
    // ensure that the status filter is set correctly based on the current view
    if (uiViewType === 'pending') {
      // only show pending_review
      statuses = ['pending_review'];
    } else if (showManagerView && statuses?.length) {
      // do not show pending_review to managers if not on that view
      statuses = statuses.filter(s => s !== 'pending_review');
    }

    params.statuses = (statuses || []).join(',') || null;

    // restrict pending when manager viewing "processed requests"
    if (showManagerView && uiViewType === 'all') {
      params.no_pending_status = true;
    }

    return params;
  }
}

class AffiliateUiSettingsModel extends UiSettingsModel {
  init(data) {
    super.init(data);

    this.company_filters = data.company_filters || [];
    this.child_affiliate_filters = data.child_affiliate_filters || [];
  }

  reset() {
    super.reset();
    this.company_filters = [];
    this.child_affiliate_filters = [];
  }
}

class TaskTemplateUiSettingsModel extends UiSettingsModel {
  init(data) {
    super.init(data);

    this.categories = data.task_categories || []; // id/name
  }

  reset() {
    super.reset();

    this.categories = null;
  }

  getParamsObj(page, limit, archived = false) {
    return {
      search: this.search,
      ordering: this.sort,
      offset: (page - 1) * limit,
      limit,
      categories: (this.categories || []).map(c => c.id).join(',') || null,
      archived,
    };
  }
}

class TemplateLibraryUiSettingsModel extends UiSettingsModel {
  init(data) {
    super.init(data);

    this.categories = data.categories || []; // id/name
    this.types = data.types || [];
    this.schedules = data.schedules || [];
    this.created_by_user_ids = data.created_by_user_ids || data.created_by?.users || [];
    this.created_by_company_ids = data.created_by_company_ids || data.created_by?.companies || [];
  }

  reset() {
    super.reset();

    this.categories = [];
    this.types = [];
    this.schedules = [];
    this.created_by_user_ids = [];
    this.created_by_company_ids = [];
  }

  getParamsObj(page, limit, archived = false) {
    return {
      search: this.search,
      ordering: this.sort,
      offset: (page - 1) * limit,
      limit,
      categories: (this.categories || []).map(c => c.id).join(',') || null,
      types: this.types.map(t => t.id).join(','),
      schedules: this.schedules.map(s => s.id).join(','),
      created_by_user_ids: this.created_by_user_ids,
      created_by_company_ids: this.created_by_company_ids,
      archived,
    };
  }

  getCreatedBy() {
    return {
      users: this.created_by_user_ids || [],
      companies: this.created_by_company_ids || [],
    };
  }
}

class ReservationRequestUiSettingsModel extends UiSettingsModel {
  init(data) {
    super.init(data);

    this.requester = data.requester ? new UserModel(data.requester) : null;
    this.reviewed_statuses = data.statuses || null;
    this.locations = (data.locations || []).map(l => l.id);
    this.date_after = data.date_after || null;
    this.date_before = data.date_before || null;
    this.reviewers = (data.reviewers ?? []).map(u => new UserModel(u));
    this.no_assigned_reviewer = data.no_assigned_reviewer ?? null;

    this.locationFilterUpdated = null;
  }

  reset(resetReviewerFilters = false) {
    super.reset();

    this.requester = 0;
    this.reviewed_statuses = null;
    this.locations = [];
    this.date_after = null;
    this.date_before = null;

    if (resetReviewerFilters) {
      this.reviewers = [];
      this.no_assigned_reviewer = null;
    }
  }

  getParamsObj(page, limit, uiViewType = 'all', showManagerView = false) {
    const dateAfter = (!this.date_after || typeof this.date_after === 'string')
      ? this.date_after
      : moment(this.date_after).utc().format(DJANGO_DATE_FORMAT);

    const dateBefore = (!this.date_before || typeof this.date_before === 'string')
      ? this.date_before
      : moment(this.date_before).utc().format(DJANGO_DATE_FORMAT);

    const params = {
      search: this.search,
      ordering: this.sort,
      offset: (page - 1) * limit,
      limit,
      location: this.locations?.length ? this.locations[0] : null,
      date_after: dateAfter,
      date_before: dateBefore,
      no_assigned_reviewer: this.no_assigned_reviewer,
    };

    if (uiViewType === 'all') {
      params.status = 'canceled,reviewed,expired';
    } else if (uiViewType === 'pending') {
      params.status = 'pending_review';
    }

    if (this.reviewed_statuses) {
      params.reviewed_statuses = this.reviewed_statuses.filter(s => s.id).map(s => s.id);
    }

    if (showManagerView) {
      params.requester = this.requester?.id || null;

      const reviewFilter = (this.reviewers || []).map(u => u.id).join(',') || null;

      if (uiViewType === 'all') {
        params.reviewed_by = reviewFilter;
      } else {
        params.no_assigned_reviewer = this.no_assigned_reviewer ?? null;
        params.reviewers = reviewFilter;
      }
    }

    return params;
  }
}

class LocationReservationsUiSettingsModel extends UiSettingsModel {
  init(data) {
    super.init(data);

    this.requester = data.requester ? new UserModel(data.requester) : null;
    this.statuses = data.statuses || null;
    this.locations = (data.locations || []).map(l => l.id);
    this.date_after = data.date_after || null;
    this.date_before = data.date_before || null;

    this.locationFilterUpdated = null;
  }

  reset() {
    super.reset();

    this.requester = 0;
    this.statuses = null;
    this.locations = [];
    this.date_after = null;
    this.date_before = null;
  }

  getFilterCount(includeLocations = false) {
    let count = 0;
    if (this.requester !== null && this.requester != 0) { count++; }
    if (this.statuses?.length) { count++; }
    if (includeLocations && this.locations?.length) { count++; }
    if (this.date_after) { count++; }
    if (this.date_before) { count++; }
    return count;
  }

  getParamsObj(page, limit, ordering = 'name', preventPastReservations = true) {
    const dateAfter = (!this.date_after || typeof this.date_after === 'string') ? this.date_after : moment(this.date_after).format(DJANGO_DATE_FORMAT);
    const dateBefore = (!this.date_before || typeof this.date_before === 'string') ? this.date_before : moment(this.date_before).format(DJANGO_DATE_FORMAT);

    this.sort = ordering;

    const params = {
      search: this.search,
      ordering: this.sort,
      offset: (page - 1) * limit,
      limit,
      location: this.locations?.length ? this.locations[0] : null,
      date_after: dateAfter,
      date_before: dateBefore,
      requesters: this.requester?.id || null,
    };

    if (this.statuses) {
      params.status = this.statuses.filter(s => s.id).map(s => s.id);
    } else {
      params.status = [ReservationStatus.APPROVED, ReservationStatus.PENDING_REVIEW];
    }

    if (preventPastReservations) {
      params.end_time__gte = (new Date()).toISOString();
    }

    return params;
  }
}

class ReservationUiSettingsModel extends UiSettingsModel {
  init(data) {
    super.init(data);

    this.sort = data.sort || 'start_time';

    this.locations = (data.locations || []).map(l => l.id);
    this.date_after = data.date_after || null;
    this.date_before = data.date_before || null;
    this.requesters = (data.requesters ?? []).map(u => new UserModel(u));
    this.statuses = (data.statuses ?? []).map(status => ReservationStatusUi[status]);

    this.locationFilterUpdated = null;
  }

  reset(resetReservedByFilter = false) {
    super.reset();

    this.locations = [];
    this.date_after = null;
    this.date_before = null;
    this.statuses = [];

    if (resetReservedByFilter) {
      this.requesters = [];
    }
  }

  getParamsObj(page, limit) {
    const params = {
      search: this.search,
      ordering: this.sort,
      offset: (page - 1) * limit,
      limit,
      location: this.locations?.length ? this.locations[0] : null,
      date_after: this._sanitizeDateParam(this.date_after),
      date_before: this._sanitizeDateParam(this.date_before),
    };

    // override status filter if param is set
    if (this.statuses?.length === 1) {
      params.status = this.statuses[0].id;
    } else if (this.statuses?.length > 1) {
      params.status = this.statuses.map(s => s.id);
    }

    if (this.requesters && this.requesters.length > 0) {
      params.requesters = this.requesters.map(u => u.id).join(',');
    }

    return params;
  }

  _sanitizeDateParam(date) {
    if (!date) {
      return null;
    }
    if (typeof date === 'string') {
      return date;
    }
    if (date && (date instanceof Date || moment.isMoment(date))) {
      return moment(date).format(DJANGO_DATE_FORMAT);
    }
  }
}

class FleetVehicleUiSettingsModel extends UiSettingsModel {
  init(data) {
    super.init(data);

    this.status_filters = data.status_filters || [];
    this.location_filter = data.location_filter || null;
    this.year_filter = data.year_filter || null;
    this.manufacturer_filter = data.manufacturer_filter || null;
    this.assigned_driver_filter = data.assigned_driver_filter || null;
    this.reservable_filter = data.reservable_filter || null;
    this.last_inspection_result_filter = data.last_inspection_result_filter || null;
    this.registration_expiry_filter = data.registration_expiry_filter || null;
  }
}

class FleetDriverProfileUiSettingsModel extends UiSettingsModel {
  init(data) {
    super.init(data);

    this.status_filters = data.status_filters || [];
    this.license_class_filters = data.license_class_filters || [];
    this.license_state_filters = data.license_state_filters || [];
    this.assigned_vehicle_filter = data.assigned_vehicle_filter || null;
    this.certification_filters = data.certification_filters || [];
    this.license_expiry_filter = data.license_expiry_filter || null;
    this.cert_expiry_filter = data.cert_expiry_filter || null;
  }
}

class FleetInspectionUiSettingsModel extends UiSettingsModel {
  init(data) {
    super.init(data);

    this.type_filters = data.type_filters || [];
    this.status_filters = data.status_filters || [];
    this.result_filters = data.result_filters || [];
    this.vehicle_filter = data.vehicle_filter || null;
    this.inspector_filter = data.inspector_filter || null;
    this.date_start_filter = data.date_start_filter || null;
    this.date_end_filter = data.date_end_filter || null;
  }
}

export {
  AffiliateUiSettingsModel,
  EquipmentUiSettingsModel,
  FleetDriverProfileUiSettingsModel,
  FleetInspectionUiSettingsModel,
  FleetVehicleUiSettingsModel,
  LocationUiSettingsModel,
  PartUiSettingsModel,
  PartContainerUiSettingsModel,
  PMScheduleUiSettingsModel,
  ReservationRequestUiSettingsModel,
  ReservationUiSettingsModel,
  LocationReservationsUiSettingsModel,
  TaskTemplateUiSettingsModel,
  TemplateLibraryUiSettingsModel,
  UiSettingsModel,
  WorkOrderRequestUiSettingsModel,
};
